import React from 'react';
import { Button } from 'carbon-components-react';
import { Checkmark32 } from '@carbon/icons-react';

export interface StatItem {
  label: string;
  value: string;
  valueClassName?: string;
}

export interface ActionButtonConfig {
  label: string;
  icon?: React.ComponentType;
  onClick: () => void;
  kind?: 'primary' | 'secondary' | 'tertiary' | 'ghost';
  disabled?: boolean;
}

interface CompletionScreenProps {
  heading: string;
  bodyText?: string;
  stats: StatItem[];
  /** Extra class on the stats row (e.g. `expand` when showing transaction reference). */
  statsRowClassName?: string;
  primaryAction?: ActionButtonConfig;
  secondaryActions?: ActionButtonConfig[];
  /** Actions rendered outside the white container, below it (e.g. "Share", "Lodge another") */
  outsideActions?: ActionButtonConfig[];
  footerSection?: React.ReactNode;
}

const CompletionScreen: React.FC<CompletionScreenProps> = ({
  heading,
  bodyText,
  stats,
  statsRowClassName,
  footerSection,
  primaryAction,
  secondaryActions,
  outsideActions
}) => {
  return (
    <div className="completion-screen">
      <div className="completion-screen__container">
        <div className="completion-screen__icon-bg">
          <Checkmark32 className="completion-screen__icon" />
        </div>
        <div className="completion-screen__heading">{heading}</div>
        {bodyText ? (
          <div className="completion-screen__body">{bodyText}</div>
        ) : null}

        {stats.length > 0 && (
          <div
            className={['completion-screen__stats-row', statsRowClassName]
              .filter(Boolean)
              .join(' ')}
          >
            {stats.map((stat, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <div className="completion-screen__stat-divider" />
                )}
                <div className="completion-screen__stat-item">
                  <div className="completion-screen__stat-label">
                    {stat.label}
                  </div>
                  <div
                    className={[
                      'completion-screen__stat-value',
                      stat.valueClassName
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {stat.value}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {primaryAction && (
          <div className="completion-screen__primary-action">
            <Button
              kind={primaryAction.kind || 'primary'}
              onClick={primaryAction.onClick}
              renderIcon={primaryAction.icon}
              disabled={primaryAction.disabled}
            >
              {primaryAction.label}
            </Button>
          </div>
        )}

        {secondaryActions && secondaryActions.length > 0 && (
          <div className="completion-screen__secondary-actions">
            {secondaryActions.map((action, index) => (
              <Button
                key={index}
                kind={action.kind || 'tertiary'}
                onClick={action.onClick}
                renderIcon={action.icon}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}

        {footerSection && (
          <div className="completion-screen__footer-section">
            {footerSection}
          </div>
        )}
      </div>

      {outsideActions && outsideActions.length > 0 && (
        <div className="completion-screen__outside-actions">
          {outsideActions.map((action, index) => (
            <Button
              key={index}
              kind={action.kind || 'tertiary'}
              onClick={action.onClick}
              renderIcon={action.icon}
              disabled={action.disabled}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompletionScreen;
